import { useCallback, useState } from 'react';

import { Printty } from '../../Printty/printty';
import { UseTablePdf } from '../types/tablePdf.types';
import { PrintTable } from './printTable';

type SaveTablePdfArgs<TData> =
  | {
      callback: () => Promise<TData>;
      data?: never;
    }
  | {
      data: TData;
      callback?: never;
    };

const useTablePdf = ({
  HeaderElement,
  HeaderInfoBoxElement,
  tableHeader,
  orientation,
  fileName,
  debug,
  columns,
  headerProps,
  pdfAction,
}: UseTablePdf) => {
  const [isTableLoading, setIsTableLoading] = useState(false);
  const { targetReactRef, isLoading, cleanupTargetFrame, action, elementClassId } =
    Printty.usePrintty({
      HeaderElement,
      HeaderInfoBoxElement,
      headerProps,
      orientation,
      debug,
      pdfAction,
    });

  /** Generates a table given an array of data, uses the provided columns to generate this data */
  const generateTable = useCallback(
    (data: unknown[]) => {
      return PrintTable.create({
        data,
        columns,
        tableHeader,
        elementClassId,
      });
    },
    [columns, tableHeader]
  );

  /**
   * Fetches the data if a callback is provided
   * otherwise it returns the data
   */
  const getPrintData = useCallback(
    async <TData extends unknown[]>(data?: TData, callback?: () => Promise<TData>) => {
      if (callback) {
        const data = await callback();
        return data as TData;
      }
      return data as TData;
    },
    []
  );

  /**
   * Creates a table given a callback or data and saves/prints it as a PDF
   *
   * @param {SaveTablePdfArgs<TData>} args - The arguments for saving the table as a PDF - Provide either a callback to fetch data or the data itself.
   */

  const handleStart = useCallback(
    async <TData extends unknown[]>({ callback, data }: SaveTablePdfArgs<TData>) => {
      setIsTableLoading(true);
      try {
        const fetchedData = await getPrintData(data, callback);
        if (!fetchedData) return;
        const table = generateTable(fetchedData);
        targetReactRef.current = table;

        await action({ fileName });
        setIsTableLoading(false);
      } catch {
        setIsTableLoading(false);
      }
    },
    [action, fileName, generateTable, getPrintData, targetReactRef]
  );
  return {
    handleStart,
    isLoading: isTableLoading || isLoading,
    cleanupTargetFrame,
  };
};

export { useTablePdf };
